import styled, { css, keyframes } from 'styled-components';
import { Icon } from '../icon';
import { mainTheme, transition } from '../themes/mainTheme';
import {
	DropdownSize, DropdownSizes, menuItemPaddingHorizontal, menuPadding
} from './DropdownSize';

// The collapse outlives isOpen, so Dropdown needs the same number in JS to know
// when the menu is finally gone. One const drives both.
export const menuMotionMs = parseInt(mainTheme.motion.duration.default, 10);

// A `from`-only keyframe animates out of the collapsed frame and into whatever
// the element's own rules say; a `to`-only one does the reverse.
const expand = keyframes`
  from {
    grid-template-rows: 0fr;
    opacity: 0;
  }
`;

const collapse = keyframes`
  to {
    grid-template-rows: 0fr;
    opacity: 0;
  }
`;

export const ItemLabel = styled.span<{
    $disabled?: boolean,
    $danger?: boolean,
}>`
  flex: 1;
  text-align: left;
  // Plain rows follow the row's own colour, which is what hover and selection
  // move. Disabled and danger opt out — they mean the same thing in every state.
  color: ${({ $disabled, $danger, theme }) => $disabled ? theme.form.dropdown.menu.disabled.color :
		($danger ? theme.form.dropdown.menu.danger.color : 'inherit')};
`;

// A row's own extra — info icon, secondary action — pinned to its end.
export const ItemTrailing = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

export const MenuItemRoot = styled.div<{
    $selected?: boolean,
    $disabled?: boolean,
    $danger?: boolean,
    $highlighted?: boolean,
    $size?: DropdownSize,
}>`
  padding: 0 ${menuItemPaddingHorizontal}px;
  // Rows are laid out by the scrollport's gap alone. A row is whatever element
  // it renders as — a <label> picks up the app stylesheet's bottom margin, and
  // the sliding HoverIndicator is sized to the row, so any extra margin becomes
  // a stripe the rectangle cannot cover.
  margin: 0;
  border-radius: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 510;
  // Rows take the trigger box's scale: a compact dropdown gets compact rows.
  ${({ $size = 'compact' }) => {
		const size = DropdownSizes[$size];
		return css`
      min-height: ${size.itemHeight}px;
      font-size: ${size.fontSize}px;
      letter-spacing: ${size.letterSpacing}px;

      ${Icon.cssSelector} {
        font-size: ${size.iconSize}px;
      }
    `;
	}}
  // Not inherited by the button and anchor rows a menu of actions renders.
  font-family: inherit;
  border: none;
  background: transparent;
  // Above the indicator sliding underneath.
  position: relative;
  color: ${props => props.theme.form.dropdown.menu.color};
  transition: ${transition('background', 'color')};

  ${Icon.cssSelector} {
    flex: 0 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.$danger
		? props.theme.form.dropdown.menu.danger.color
		: props.theme.form.dropdown.menu.icon};
  }

  // The chosen row rests at the brightness the pointer gives every other row,
  // and holds it — there is no checkmark to carry the selection.
  ${({ $selected, theme }) => $selected && css`
    color: ${theme.form.dropdown.menu.selected};
  `};

  ${({ $disabled }) => $disabled ? css`
    cursor: not-allowed;
  ` : css`
    cursor: pointer;

    // The hover fill lives in HoverIndicator, which slides between rows;
    // a row only lifts its own text.
    &:hover {
      text-decoration: none;
      color: ${props => props.theme.form.dropdown.menu.hover.color};
    }

    &:active {
      background: ${props => props.theme.form.dropdown.menu.pressed.background};
      color: ${props => props.theme.form.dropdown.menu.pressed.color};
      box-shadow: none;
    }
  `};

  // Keyboard highlight is the pointer's hover, driven from state instead.
  ${({ $highlighted, theme }) => $highlighted && css`
    color: ${theme.form.dropdown.menu.hover.color};
  `};
`;

export const MenuRoot = styled.div<{ $closing?: boolean, $minWidth?: number }>`
  // A sticky menu is given the trigger's width inline, and only a min-width
  // outranks that — which is what lets a narrow trigger still open a readable
  // menu. Everything here is otherwise sized by the popper.
  ${({ $minWidth }) => $minWidth !== undefined && css`
    min-width: ${$minWidth}px;
  `}
  display: grid;
  grid-template-rows: 1fr;
  border-radius: 4px;
  box-shadow: ${props => props.theme.form.dropdown.menu.shadow};
  z-index: 45;

  // The row track carries the whole open and close motion: 0fr collapses it to
  // nothing, 1fr lets the content size it. Filling forwards holds the collapsed
  // frame for the moment between the animation ending and the unmount.
  animation: ${props => props.$closing ? collapse : expand}
    ${menuMotionMs}ms
    ${props => props.theme.motion.easing.default} forwards;

  // A closing menu is still on screen, and clicking a row that is on its way
  // out selects something the user is watching disappear.
  ${({ $closing }) => $closing && css`
    pointer-events: none;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// The menu's surface. Everything that is not a scrolling row — a search field,
// say — sits on this plane next to the scrollport instead of inside it, so it
// stays put while the rows move under it.
export const MenuPanel = styled.div`
  font-family: ${props => props.theme.font};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.form.dropdown.menu.default};
  border-radius: 4px;
  // Inset highlight instead of a stroke — the menu lifts off the panel behind
  // it rather than being drawn onto it.
  box-shadow: ${props => props.theme.shadow.inset};
  color: ${props => props.theme.form.dropdown.menu.color};
  // Lets the grid row above collapse past the content height.
  min-height: 0;
  overflow: hidden;
`;

export const MenuOverflow = styled.div<{ $size?: DropdownSize }>`
  font-family: ${props => props.theme.font};
  box-sizing: border-box;
  background: ${props => props.theme.form.dropdown.menu.default};
  border-radius: 4px;
  // Inset highlight instead of a stroke — the menu lifts off the panel behind
  // it rather than being drawn onto it.
  box-shadow: ${props => props.theme.shadow.inset};

  // On a panel the surface is already drawn; the scrollport is only a scrollport.
  ${MenuPanel} > & {
    background: none;
    border-radius: 0;
    box-shadow: none;
  }

  display: flex;
  flex-direction: column;
  // No gap between rows: the sliding HoverIndicator is sized to one row, so any
  // space between them is a stripe the rectangle skips over on its way. Rows
  // carry their own height instead.
  padding: ${menuPadding}px;
  color: ${props => props.theme.form.dropdown.menu.color};
  max-height: ${({ $size = 'compact' }) => DropdownSizes[$size].menuMaxHeight}px;
  // Positioning context for HoverIndicator: offsetTop of a row is measured
  // against this box, so the indicator scrolls with the rows.
  position: relative;
  // Lets the grid row above collapse past the content height.
  min-height: 0;
  overflow: auto;

  // Menu measures the rows and sets this box's height inline, so a search that
  // filters the list slides the menu between sizes instead of snapping to the
  // new one. Unset until that first measurement — auto does not interpolate,
  // which is what keeps opening a menu the expand animation alone.
  transition: ${transition('height')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// The rows in one box, so the scrollport can be given their height: once it
// has one, its own scrollHeight is no longer the rows' — this box still is.
export const MenuContent = styled.div`
  display: flex;
  flex-direction: column;
  // The scrollport is a column too, and a flex item shrinks by default: the
  // rows would be squeezed to its height instead of scrolling past it.
  flex: 0 0 auto;
`;

// One rectangle for the whole menu, moved to whichever row the pointer (or the
// keyboard) is on, instead of a fill per row lighting up in place.
export const HoverIndicator = styled.div<{ $visible?: boolean }>`
  position: absolute;
  left: ${menuPadding}px;
  right: ${menuPadding}px;
  top: 0;
  border-radius: 2px;
  pointer-events: none;
  background: ${props => props.theme.form.dropdown.menu.hover.background};
  box-shadow: ${props => props.theme.shadow.inset};
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: ${props => ['transform', 'height', 'opacity']
		.map(prop => `${prop} ${props.theme.motion.duration.instant} ${props.theme.motion.easing.default}`)
		.join(', ')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
